import React, { useId } from 'react';
import { Avatar, Button, Tab, Tabs } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FollowPlayerButton from '../scouting/FollowPlayerButton';
import AdvancedStatistics from '../advancedStatistics/AdvancedStatistics';
import PlayerParticipationRates from './PlayerParticipationRates';
import useAdvancedMatch from '../../hooks/queries/useAdvancedMatch';
import { advancedPlayerIdentity } from '../../utils/advancedStatistics';
import { playerName, playerMetric } from '../../utils/playerRanking';
import { playerComparisonUrl, playerDiscoveryUrl, playerPosition } from '../../utils/playerDiscovery';
import { aggregatePlayerStatistics, canonicalPlayerStatistics, playerCareerRows, playerGoalIntervals,
  playerGoalRounds, playerStatisticValue } from '../../utils/playerStatistics';

const PARTICIPATION = ['partidosJugados', 'minutosJugados', 'titularidades', 'convocatorias'];
const DISCIPLINE = ['amarillasTotales', 'segundasAmarillasTotales', 'rojasTotales', 'puntosDisciplinarios'];
const SECTIONS = ['participation', 'goals', 'discipline', 'advanced'];

export function PlayerStatisticsIdentity(props) {
  return props.jugador.estadisticasAvanzadas
    ? <PlayerIdentityWithMatch {...props} /> : <PlayerIdentityContent {...props} />;
}

function PlayerIdentityWithMatch({ jugador, ...props }) {
  const { acta } = useAdvancedMatch(jugador);
  return <PlayerIdentityContent {...props} jugador={advancedPlayerIdentity(jugador, acta)} />;
}

function PlayerIdentityContent({ jugador, season, profileLink = false }) {
  const { t } = useTranslation('players');
  const { t: d } = useTranslation('directories');
  const discoveryUrl = playerDiscoveryUrl(jugador);
  const comparisonUrl = playerComparisonUrl(jugador);
  const titleId = useId();
  const name = playerName(jugador);
  const team = jugador.nombreEquipo || jugador.equipo;
  const positionName = playerPosition(jugador);
  return <section className="dr-panel ps-identity" aria-labelledby={titleId}>
    <Avatar src={jugador.foto || undefined} alt="" className="ps-avatar">{name[0]}</Avatar>
    <div className="ps-identity-text"><span className="ps-eyebrow">{t('modern.playerDossier')}</span>
      <h2 id={titleId}>{name}</h2>
      <div className="ps-context">{team && <span>{team}</span>}
        <span>{jugador.esPortero ? t('position.goalkeeper') : positionName || t('position.player')}</span>
        {Number(jugador.edad) > 0 && <span>{t('ageLabel', { age: jugador.edad })}</span>}
        {season && <span>{t('modern.season', { season })}</span>}</div>
    </div>
    <div className="ps-identity-actions"><FollowPlayerButton jugadorId={jugador.idJugador}
      jugadorNombre={name} equipoNombre={team} size="medium" />
      {discoveryUrl && <Button component={Link} variant="outlined" to={discoveryUrl}>
        {d('discovery.similarAction')}
      </Button>}
      {comparisonUrl && <Button component={Link} variant="outlined" to={comparisonUrl}>
        {d('comparePlayers')}
      </Button>}
      {profileLink && <Button component={Link} variant="outlined" endIcon={<ArrowForward />}
        to={`/estadisticas?${new URLSearchParams({ idJugador: jugador.idJugador ?? jugador.id })}`}
        state={{ jugador }}>{t('modern.fullProfile')}</Button>}</div>
  </section>;
}

function Metrics({ player, keys, accent = false }) {
  const { t, i18n } = useTranslation('players');
  return <dl className={`ps-metrics ${accent ? 'ps-metrics-accent' : ''}`}>{keys.map(key => <div key={key}>
    <dt>{t(`modern.metrics.${key}`)}</dt><dd>{playerMetric(player, key).toLocaleString(i18n.language, {
      maximumFractionDigits: key === 'golesPorPartido' ? 2 : key === 'minutosPorGol' ? 1 : 0,
    })}</dd></div>)}</dl>;
}

function DataTable({ title, rows, columns }) {
  const { i18n } = useTranslation('players');
  return <div className="ps-table-scroll"><table className="ps-table" aria-label={title}>
    <thead><tr>{columns.map(column => <th scope="col" key={column}>{column}</th>)}</tr></thead>
    <tbody>{rows.map(([label, ...values], index) => <tr key={`${label}:${index}`}><th scope="row">{label}</th>
      {values.map((value, cell) => <td key={cell}>{typeof value === 'number' ? value.toLocaleString(i18n.language, {
        maximumFractionDigits: 2,
      }) : value}</td>)}</tr>)}</tbody></table></div>;
}

export default function PlayerStatistics({ jugador }) {
  const { t, i18n } = useTranslation('players');
  const prefix = useId();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const sections = SECTIONS.filter(key => key !== 'advanced' || jugador.estadisticasAvanzadas);
  const section = sections.includes(params.get('section')) ? params.get('section') : 'participation';
  const changeSection = (_, value) => {
    const next = new URLSearchParams(params);
    if (value === 'participation') next.delete('section');
    else next.set('section', value);
    setParams(next, { state: location.state, preventScrollReset: true });
  };
  const player = canonicalPlayerStatistics(jugador);
  const intervals = playerGoalIntervals(jugador);
  const rounds = playerGoalRounds(jugador);
  const maxGoals = Math.max(1, ...intervals.map(row => row.total));
  const venues = [t('modern.total'), t('modern.home'), t('modern.away')];
  const participationRows = ['partidosJugados', 'minutosJugados', 'titularidades', 'convocatorias'].map(key => {
    const section = key === 'minutosJugados' ? 'minutos' : 'partidos';
    const home = playerStatisticValue(jugador, section, `${key}Local`);
    const away = playerStatisticValue(jugador, section, `${key}Visitante`);
    return [t(`modern.metrics.${key}`), home + away, home, away];
  });
  const goalRows = [['golesTotales', 'golesTotales'], ['golesPenaltis', 'golesTotalesPenalti']].map(([label, field]) => {
    const home = playerStatisticValue(jugador, 'goles', `${field}Local`) +
      (field === 'golesTotales' ? playerStatisticValue(jugador, 'goles', 'golesTotalesPenaltiLocal') : 0);
    const away = playerStatisticValue(jugador, 'goles', `${field}Visitante`) +
      (field === 'golesTotales' ? playerStatisticValue(jugador, 'goles', 'golesTotalesPenaltiVisitante') : 0);
    return [t(`modern.metrics.${label}`), playerMetric(player, label), home, away];
  });
  const situations = ['AbreMarcador', 'Empatando', 'Ganando', 'Perdiendo'].map(context => {
    const home = playerStatisticValue(jugador, 'golesExtra', `goles${context}Local`) +
      playerStatisticValue(jugador, 'golesExtra', `goles${context}LocalPenalti`);
    const away = playerStatisticValue(jugador, 'golesExtra', `goles${context}Visitante`) +
      playerStatisticValue(jugador, 'golesExtra', `goles${context}VisitantePenalti`);
    return [t(`modern.situations.${context}`), home + away, home, away];
  });
  const cardRows = [['amarillasTotales', 'tarjetasAmarillas'], ['segundasAmarillasTotales', 'segundasAmarillas'],
    ['rojasTotales', 'tarjetasRojas']].map(([label, field]) => {
    const venueCount = venue => ['Ganando', 'Empatando', 'Perdiendo']
      .reduce((sum, context) => sum + playerStatisticValue(jugador, 'tarjetas', `${field}${context}${venue}`), 0);
    return [t(`modern.metrics.${label}`), playerMetric(player, label), venueCount('Local'), venueCount('Visitante')];
  });
  return <div className="ps-statistics">
    <Metrics player={player} keys={['partidosJugados', 'minutosJugados', 'golesTotales', 'puntosDisciplinarios']} accent />
    <Tabs className="ps-tabs" value={section} onChange={changeSection}
      variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label={t('modern.sectionsLabel')}>
      {sections.map(key => <Tab key={key} value={key} id={`${prefix}-tab-${key}`}
        aria-controls={`${prefix}-panel-${key}`} label={t(key === 'advanced' ? 'advanced:title' : `modern.sections.${key}`)} />)}
    </Tabs>
    <div className="ps-tab-panel" role="tabpanel" tabIndex={0} id={`${prefix}-panel-${section}`}
      aria-labelledby={`${prefix}-tab-${section}`}>
    {section === 'participation' && <section className="dr-panel ps-section" id={`${prefix}-participation`}>
      <header><span className="ps-eyebrow">{t('modern.performance')}</span><h2>{t('modern.sections.participation')}</h2>
        <p>{t('modern.participationSubtitle')}</p></header>
      <Metrics player={player} keys={PARTICIPATION} />
      <DataTable title={t('modern.participationTable')} rows={participationRows}
        columns={[t('modern.metric'), ...venues]} />
      <PlayerParticipationRates player={jugador} />
    </section>}
    {section === 'goals' && <section className="dr-panel ps-section" id={`${prefix}-goals`}>
      <header><span className="ps-eyebrow">{t('modern.attack')}</span><h2>{t('modern.sections.goals')}</h2>
        <p>{t('modern.goalsSubtitle')}</p></header>
      <Metrics player={player} keys={['golesTotales', 'golesPenaltis', 'minutosPorGol', 'golesPorPartido']} />
      <DataTable title={t('modern.goalsTable')} rows={goalRows} columns={[t('modern.metric'), ...venues]} />
      <div className="ps-two-columns"><div><h3>{t('modern.goalIntervals')}</h3>
        <div className="ps-intervals" aria-hidden="true">{intervals.map(row => <div key={row.label}>
          <strong>{row.total.toLocaleString(i18n.language)}</strong><div className="ps-bar-track">
            <div className="ps-bar-home" style={{ height: `${row.home / maxGoals * 100}%` }} />
            <div className="ps-bar-away" style={{ height: `${row.away / maxGoals * 100}%` }} /></div><small>{row.label}</small>
        </div>)}</div>
        <p className="ps-legend"><span>{t('modern.home')}</span><span>{t('modern.away')}</span></p>
        <DataTable title={t('modern.goalIntervals')} rows={intervals.map(row => [row.label, row.total, row.home, row.away])}
          columns={[t('modern.interval'), ...venues]} /></div>
        <div><h3>{t('modern.goalSituations')}</h3><DataTable title={t('modern.goalSituations')} rows={situations}
          columns={[t('modern.situation'), ...venues]} />
          <h3>{t('modern.goalRounds')}</h3>{rounds.length ? <DataTable title={t('modern.goalRounds')}
            rows={rounds.map(row => [t('modern.round', { round: row.round }), row.total])}
            columns={[t('modern.matchday'), t('modern.metrics.golesTotales')]} />
            : <p className="ps-note">{t('modern.noGoalRounds')}</p>}</div></div>
    </section>}
    {section === 'discipline' && <section className="dr-panel ps-section" id={`${prefix}-discipline`}>
      <header><span className="ps-eyebrow">{t('modern.disciplineEyebrow')}</span><h2>{t('modern.sections.discipline')}</h2>
        <p>{t('modern.disciplineSubtitle')}</p></header>
      <Metrics player={player} keys={DISCIPLINE} />
      <DataTable title={t('modern.disciplineTable')} rows={cardRows} columns={[t('modern.metric'), ...venues]} />
    </section>}
    {section === 'advanced' && <AdvancedStatistics key={jugador.id} record={jugador} id={`${prefix}-advanced`} />}
    </div>
    <p className="ps-note">{t('modern.missingNote')}</p>
  </div>;
}

export function PlayerCareerSummary({ estadisticas = [], temporadaSeleccionada }) {
  const { t, i18n } = useTranslation('players');
  const rows = estadisticas.filter(row => !temporadaSeleccionada || String(row.idTemporada) === String(temporadaSeleccionada));
  const groups = [['overall', rows], ['football11', rows.filter(row =>
    row.competicion?.idTipoJuego != null && String(row.competicion.idTipoJuego) !== '2')],
  ['football7', rows.filter(row => String(row.competicion?.idTipoJuego) === '2')]];
  return <div className="ps-career-summary">{groups.filter(([key, values]) => key === 'overall' || values.length)
    .map(([key, values]) => {
      const player = aggregatePlayerStatistics(values);
      return <section className="dr-panel ps-section" key={key} aria-label={t(`modern.career.${key}`)}>
        <header><span className="ps-eyebrow">{t('modern.career.summary')}</span><h2>{t(`modern.career.${key}`)}</h2></header>
        <Metrics player={player} keys={['golesTotales', 'golesPenaltis', 'partidosJugados', 'minutosJugados',
          'titularidades', 'convocatorias', 'puntosDisciplinarios', 'rojasTotales']} />
        <dl className="ps-extra-metrics">{['vecesCapitan', 'golesTitular', 'golesSuplente'].map(metric => {
          const section = metric === 'vecesCapitan' ? 'partidos' : 'goles';
          const count = values.reduce((sum, row) => sum + playerStatisticValue(row, section, `${metric}Local`) +
            playerStatisticValue(row, section, `${metric}Visitante`), 0);
          return <div key={metric}><dt>{t(`modern.metrics.${metric}`)}</dt>
            <dd>{count.toLocaleString(i18n.language)}</dd></div>;
        })}</dl>
      </section>;
    })}</div>;
}

export function PlayerCareerTable({ estadisticas = [], temporadas = [], temporadaSeleccionada }) {
  const { t, i18n } = useTranslation('players');
  const location = useLocation();
  const rows = playerCareerRows(estadisticas, temporadaSeleccionada);
  const metrics = ['golesTotales', 'golesPenaltis', 'partidosJugados', 'convocatorias', 'titularidades', 'minutosJugados'];
  return <div className="ps-career-history">
    <table className="ps-table ps-career-table" aria-label={t('modern.career.history')}>
    <colgroup><col className="ps-career-season" /><col className="ps-career-team" /><col />
      {metrics.map(key => <col key={key} className={`ps-career-${key}`} />)}<col className="ps-career-action" /></colgroup>
    <thead><tr>{['season', 'team', 'competition'].map(key => <th key={key} scope="col" className="ps-career-text">{t(`modern.career.${key}`)}</th>)}
      {metrics.map(key => <th key={key} scope="col" aria-label={t(`modern.metrics.${key}`)}>
        <abbr title={t(`modern.metrics.${key}`)}>{t(`modern.career.columns.${key}`)}</abbr></th>)}
      <th scope="col">{t('modern.career.details')}</th></tr></thead>
    <tbody>{rows.map((row, index) => <tr key={`${row.id}:${index}`}><th scope="row">
      {temporadas.find(season => String(season.id) === String(row.idTemporada))?.nombre ||
        t('modern.season', { season: row.idTemporada })}</th>
      <td className="ps-career-text" data-label={t('modern.career.team')}>{row.nombreEquipo || row.equipo || t('modern.notRecorded')}</td>
      <td className="ps-career-text" data-label={t('modern.career.competition')}>{row.competicion?.nombre || t('modern.notRecorded')}</td>
      {metrics.map(key => <td key={key} data-label={t(`modern.metrics.${key}`)}>{playerMetric(row, key).toLocaleString(i18n.language)}</td>)}
      <td><Button component={Link} to={`/jugador?${new URLSearchParams({ id: row.id ?? row.idJugador, idTemporada: row.idTemporada })}`}
        state={{ directoryFrom: location.pathname + location.search }} aria-label={t('modern.career.openSeason', { season: row.idTemporada })}>
        {t('modern.career.details')}<ArrowForward fontSize="small" /></Button></td></tr>)}</tbody>
  </table>{!rows.length && <p className="ps-note">{t('modern.career.empty')}</p>}</div>;
}
